/**
 * Motor de audio del juego: sintetiza SFX cortos y una música de fondo en
 * loop directamente con la Web Audio API, sin un solo archivo que cargar —
 * mismo espíritu que `TextureFactory` (nunca bloquearse esperando assets).
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en core/": este archivo NO
 * importa Phaser (usa únicamente la Web Audio API del navegador), pero
 * tampoco es "lógica pura" testeable con Vitest porque crea nodos de audio
 * reales — mismo motivo por el que `TextureFactory` tampoco tiene test
 * propio. La persistencia del mute (`audioPrefs.ts`) sí es pura y está
 * testeada aparte. Fuera de un navegador (Vitest corre con
 * `environment: 'node'`) todos los métodos son no-ops seguros: no hay
 * `window`/`AudioContext`, así que nunca se crea nada.
 */

import { getDefaultStorage, type SaveStorage } from './save';
import { loadAudioPrefs, saveAudioPrefs } from './audioPrefs';

interface ToneOptions {
  type?: OscillatorType;
  /** Ganancia pico (0..1), antes de pasar por el bus de SFX (ya bajo de por sí). */
  gain?: number;
  /** Si se define, la frecuencia se desliza exponencialmente hacia este valor durante la nota. */
  slideTo?: number;
}

interface NoiseOptions {
  gain?: number;
  filterFreq?: number;
}

/** Acordes (frecuencias en Hz) de la música de fondo: progresión alegre I-vi-IV-V en Do mayor. */
const MUSIC_CHORDS: readonly (readonly number[])[] = [
  [261.63, 329.63, 392.0], // Do mayor
  [220.0, 261.63, 329.63], // La menor
  [174.61, 220.0, 349.23], // Fa mayor
  [196.0, 246.94, 392.0], // Sol mayor
];
const MUSIC_STEP_MS = 1900;

function resolveAudioContextCtor(): (new () => AudioContext) | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as {
    AudioContext?: new () => AudioContext;
    webkitAudioContext?: new () => AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

export class AudioManager {
  private ctx: AudioContext | undefined;
  private masterGain: GainNode | undefined;
  private sfxGain: GainNode | undefined;
  private musicGain: GainNode | undefined;
  private musicTimer: ReturnType<typeof setInterval> | undefined;
  private musicStep = 0;
  private muted: boolean;
  private unlocked = false;
  private readonly lastPlayedMs = new Map<string, number>();

  constructor(private readonly storage: SaveStorage = getDefaultStorage()) {
    this.muted = loadAudioPrefs(storage).muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Desbloquea el audio del navegador (política de autoplay: solo se puede
   * crear/reanudar un `AudioContext` en respuesta a un gesto real del
   * usuario). Llamar una vez, en el primer `pointerdown` de la app. Si el
   * sonido no está silenciado, arranca la música de fondo.
   */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    const ctx = this.ensureContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
    if (!this.muted) this.startMusic();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    saveAudioPrefs({ muted }, this.storage);
    if (this.masterGain) this.masterGain.gain.value = muted ? 0 : 1;
    if (muted) {
      this.stopMusic();
    } else if (this.unlocked) {
      this.startMusic();
    }
  }

  /** Alterna mute/sonido y devuelve el nuevo estado (para que la UI refresque su ícono). */
  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  // -- SFX ----------------------------------------------------------------

  /** Disparo del héroe (autofire). */
  playShoot(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted || !this.canPlay('shoot', 60)) return;
    this.tone(ctx.currentTime, 560, 0.09, { type: 'square', gain: 0.16, slideTo: 260 });
  }

  /** Impacto: golpe recibido por un enemigo o por el héroe. `low=true` para el "ay" del héroe. */
  playHit(low = false): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted || !this.canPlay('hit', 45)) return;
    this.noiseBurst(ctx.currentTime, 0.07, { gain: 0.22, filterFreq: low ? 750 : 1500 });
  }

  /** Bola asentada en el vaso receptor del plinko ("moneda"). */
  playCoin(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted || !this.canPlay('coin', 70)) return;
    const t = ctx.currentTime;
    this.tone(t, 880, 0.07, { type: 'triangle', gain: 0.14 });
    this.tone(t + 0.05, 1318.5, 0.09, { type: 'triangle', gain: 0.16 });
  }

  /** Bola cruza una barra multiplicadora (valor revelado). El tono sube un poco con el valor. */
  playMultiplier(value = 2): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted || !this.canPlay('mult', 55)) return;
    const freq = 620 + Math.min(Math.max(value, 1), 7) * 45;
    this.tone(ctx.currentTime, freq, 0.08, { type: 'sine', gain: 0.15 });
  }

  /** Fin de run ganada: arpegio ascendente corto. */
  playVictory(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted) return;
    const t = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => this.tone(t + i * 0.11, freq, 0.24, { type: 'triangle', gain: 0.2 }));
  }

  /** Fin de run perdida: tres notas descendentes en tono menor. */
  playDefeat(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted) return;
    const t = ctx.currentTime;
    const notes = [392.0, 349.23, 293.66];
    notes.forEach((freq, i) =>
      this.tone(t + i * 0.17, freq, 0.32, { type: 'sawtooth', gain: 0.13, slideTo: freq * 0.82 })
    );
  }

  // -- Música ---------------------------------------------------------------

  /** Arranca (si no estaba sonando) el loop de música de fondo, un acorde alegre en bucle. */
  startMusic(): void {
    const ctx = this.ensureContext();
    if (!ctx || this.muted || this.musicTimer) return;
    this.playMusicStep();
    this.musicTimer = setInterval(() => this.playMusicStep(), MUSIC_STEP_MS);
  }

  stopMusic(): void {
    if (this.musicTimer) {
      clearInterval(this.musicTimer);
      this.musicTimer = undefined;
    }
  }

  private playMusicStep(): void {
    const ctx = this.ctx;
    const bus = this.musicGain;
    if (!ctx || !bus) return;
    const chord = MUSIC_CHORDS[this.musicStep % MUSIC_CHORDS.length]!;
    this.musicStep++;
    const t = ctx.currentTime + 0.05;
    chord.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const peak = 0.8 - i * 0.12;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.05, peak), t + 0.3 + i * 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.7);
      osc.connect(gain);
      gain.connect(bus);
      osc.start(t);
      osc.stop(t + 1.75);
    });
  }

  // -- Motor interno --------------------------------------------------------

  private ensureContext(): AudioContext | undefined {
    if (this.ctx) return this.ctx;
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) return undefined;
    const ctx = new Ctor();

    const masterGain = ctx.createGain();
    masterGain.gain.value = this.muted ? 0 : 1;
    masterGain.connect(ctx.destination);

    const sfxGain = ctx.createGain();
    sfxGain.gain.value = 0.5;
    sfxGain.connect(masterGain);

    const musicGain = ctx.createGain();
    musicGain.gain.value = 0.055;
    musicGain.connect(masterGain);

    this.ctx = ctx;
    this.masterGain = masterGain;
    this.sfxGain = sfxGain;
    this.musicGain = musicGain;
    return ctx;
  }

  /** Limitador simple: evita que sonidos de la misma familia se amontonen (p. ej. avalancha de bolas). */
  private canPlay(key: string, minIntervalMs: number): boolean {
    const ctx = this.ctx;
    if (!ctx) return false;
    const now = ctx.currentTime * 1000;
    const last = this.lastPlayedMs.get(key) ?? -Infinity;
    if (now - last < minIntervalMs) return false;
    this.lastPlayedMs.set(key, now);
    return true;
  }

  private tone(startTime: number, freq: number, duration: number, options: ToneOptions = {}): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;

    const osc = ctx.createOscillator();
    osc.type = options.type ?? 'square';
    osc.frequency.setValueAtTime(freq, startTime);
    if (options.slideTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, options.slideTo), startTime + duration);
    }

    const gain = ctx.createGain();
    const peak = options.gain ?? 0.2;
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(peak, startTime + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    osc.connect(gain);
    gain.connect(bus);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  }

  private noiseBurst(startTime: number, duration: number, options: NoiseOptions = {}): void {
    const ctx = this.ctx;
    const bus = this.sfxGain;
    if (!ctx || !bus) return;

    const sampleCount = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, sampleCount, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < sampleCount; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / sampleCount);
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = options.filterFreq ?? 1200;

    const gain = ctx.createGain();
    const peak = options.gain ?? 0.3;
    gain.gain.setValueAtTime(peak, startTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    source.start(startTime);
  }
}

/** Instancia singleton compartida por toda la app (mismo patrón que `gameEvents`). */
export const audio = new AudioManager();
